import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, CornerDownLeft, User, FileText, Users } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const NAV_BY_ROLE = {
  client: [
    { label: "Dashboard", to: "/dashboard" },
    { label: "Offers", to: "/offers" },
    { label: "Talents", to: "/client/talents" },
    { label: "Bookings", to: "/client/bookings" },
    { label: "Messages", to: "/messages" },
    { label: "Support", to: "/support" },
    { label: "Account & privacy", to: "/account/privacy" },
  ],
  talent: [
    { label: "Dashboard", to: "/dashboard" },
    { label: "Gigs", to: "/talent/gigs" },
    { label: "My Offers", to: "/talent/offers" },
    { label: "Bookings", to: "/talent/bookings" },
    { label: "Messages", to: "/messages" },
    { label: "Credits", to: "/credits" },
    { label: "Settings", to: "/talent/settings" },
    { label: "Support", to: "/support" },
    { label: "Account & privacy", to: "/account/privacy" },
  ],
  agency: [
    { label: "Dashboard", to: "/dashboard" },
    { label: "Roster", to: "/agency/roster" },
    { label: "Gigs", to: "/talent/gigs" },
    { label: "Offers", to: "/talent/offers" },
    { label: "Bookings", to: "/talent/bookings" },
    { label: "Messages", to: "/messages" },
    { label: "Credits", to: "/credits" },
    { label: "Settings", to: "/agency/settings" },
    { label: "Support", to: "/support" },
    { label: "Account & privacy", to: "/account/privacy" },
  ],
  admin: [
    { label: "Dashboard", to: "/dashboard" },
    { label: "Talents", to: "/admin/talents" },
    { label: "Leads", to: "/admin/leads" },
    { label: "Reviews", to: "/admin/reviews" },
    { label: "Users", to: "/admin/users" },
    { label: "Support", to: "/admin/support" },
    { label: "Moderation", to: "/admin/moderation" },
    { label: "Email log", to: "/admin/email-logs" },
    { label: "Email templates", to: "/admin/email-templates" },
    { label: "Content blocks", to: "/admin/cms" },
    { label: "Translations", to: "/admin/translations" },
    { label: "Data & Archive", to: "/admin/archive" },
    { label: "Activity log", to: "/admin/activity" },
  ],
};

const CommandPalette = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ talents: [], leads: [], users: [] });
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const navActions = NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.client;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
      if (e.key === "Escape") setOpen(false);
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("talentsio:openPalette", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("talentsio:openPalette", onOpen); };
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); else { setQ(""); setRes({ talents: [], leads: [], users: [] }); } }, [open]);

  const search = useCallback(async (term) => {
    if (!isAdmin || term.trim().length < 2) { setRes({ talents: [], leads: [], users: [] }); return; }
    try { const { data } = await api.get("/admin/search", { params: { q: term } }); setRes(data); } catch (e) {}
  }, [isAdmin]);

  useEffect(() => { const t = setTimeout(() => search(q), 250); return () => clearTimeout(t); }, [q, search]);

  const go = (to) => { setOpen(false); navigate(to); };
  const filteredNav = navActions.filter((a) => a.label.toLowerCase().includes(q.toLowerCase()));

  if (!open) return null;
  const Row = ({ icon: Icon, label, sub, onClick }) => (
    <button onClick={onClick} data-testid="palette-result" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#1c1c1e] transition-colors">
      <Icon size={15} className="text-neutral-500 shrink-0" />
      <span className="text-white text-[14px] truncate">{label}</span>
      {sub && <span className="text-[12px] text-neutral-500 ml-auto truncate">{sub}</span>}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[12vh] px-4" data-testid="command-palette" onClick={() => setOpen(false)}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-[620px] rounded-2xl border border-[#2A2A2A] bg-[#141414] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 h-14 border-b border-[#2A2A2A]">
          <Search size={18} className="text-neutral-500" />
          <input ref={inputRef} data-testid="palette-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={isAdmin ? "Search talents, leads, users or jump to a page..." : "Jump to a page..."}
            className="flex-1 bg-transparent text-white text-[15px] placeholder-neutral-600 focus:outline-none" />
          <kbd className="text-[11px] text-neutral-500 border border-[#2A2A2A] rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto py-2">
          {filteredNav.length > 0 && <div className="px-4 pt-1 pb-1 text-[11px] uppercase tracking-wide text-neutral-600">Go to</div>}
          {filteredNav.map((a) => <Row key={a.to} icon={CornerDownLeft} label={a.label} onClick={() => go(a.to)} />)}
          {res.talents.length > 0 && <div className="px-4 pt-2 pb-1 text-[11px] uppercase tracking-wide text-neutral-600">Talents</div>}
          {res.talents.map((t) => <Row key={t.id} icon={User} label={t.display_name || t.id} sub={t.talent_type} onClick={() => go(`/t/${t.id}`)} />)}
          {res.leads.length > 0 && <div className="px-4 pt-2 pb-1 text-[11px] uppercase tracking-wide text-neutral-600">Leads</div>}
          {res.leads.map((l) => <Row key={l.id} icon={FileText} label={l.title || l.id} sub={`${l.event_city || ""} ${l.status || ""}`} onClick={() => go("/admin/leads")} />)}
          {res.users.length > 0 && <div className="px-4 pt-2 pb-1 text-[11px] uppercase tracking-wide text-neutral-600">Users</div>}
          {res.users.map((u) => <Row key={u.user_id} icon={Users} label={u.name || u.email} sub={u.role} onClick={() => go("/admin/users")} />)}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
